import { CloseOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Divider,
  Dropdown,
  Flex,
  Input,
  message,
  Modal,
  Tooltip,
  Typography,
} from 'antd';
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { trackButtonClick } from '../../analytics/mixpanel';
import { ChatbotIcon, UserIcon } from '../../assets';
import DislikeIcon from '../../assets/DislikeIcon';
import ClearBrushOutlinedRed from '../../assets/docchat/ClearBrushOutlinedRed';
import DownloadOutlined from '../../assets/docchat/DownloadOutlined';
import MSWordIcon from '../../assets/docchat/MSWordIcon';
import PdfIcon2 from '../../assets/docchat/PdfIcon2';
import LikeIcon from '../../assets/LikeIcon';
import MagicIconSVG from '../../assets/MagicIconSVG';
import AppTabs from '../../CommonComponents/AppTabs/AppTabs';
import CopyButton from '../../CommonComponents/CopyButton/CopyButton';
import InlineStyled from '../../CommonComponents/InlineStyled/InlineStyled';
import ResizableTextArea from '../../CommonComponents/ResizableTextArea/ResizableTextArea';
import AppButton from '../../CommonComponents/UI/AppButton/AppButton';
import { useActionState } from '../../CommonFuctions/useActionState';
import { useReverseInfiniteScroll } from '../../CommonFuctions/useReverseInfiniteScroll';
import useWebSocket from '../../CommonFuctions/useWebSocket';
import { copyToClipboardWithFormat, timeAgo } from '../../CommonFuctions/Utils';
import { DOCCHAT_ROOT_URL } from '../../config';
import { ACTIVE_TAB_MAPPING, DOCCHAT_ACTIVE_TAB_KEY } from '../../Constants/commonConstants';
import { deleteChat, sendFeedback, setNewMessage } from '../../Redux/DocChat/docChatActions';
import { useLocalStorage } from '../../utils/hooks/useLocalStorage/useLocalStorage';
import { useTakeTour } from '../TakeTour/hooks/useTakeTour';
import BotInitialChat from './BotInitialChat';
import ChatBoxWorkflowSelect from './ChatBoxWorkflowSelect/ChatBoxWorkflowSelect';
import LargeResponseDownload from './LargeResponseDownload';
import PresetResponseBox from './PresetResponseBox';
import { useExportDocument } from './useExportDocument';
import { usePresets } from './usePresets';
import { useSelectedUploadData } from './useSelectedUploadData';

const actionStateMapping = {
  unanalyzedFilesModal: 'unanalyzedFilesModal',
};

// The backend sends the whole history in one payload, so paging is purely a
// frontend concern: mount the newest 5 question/answer pairs and reveal older
// pages as the reader scrolls up.
const CHAT_PAGE_PAIRS = 5;
const CHAT_PAGE_SIZE = CHAT_PAGE_PAIRS * 2;
const LOAD_OLDER_THRESHOLD_PX = 200;

const MessageMarkdown = memo(function MessageMarkdown({ content }) {
  return <ReactMarkdown>{content}</ReactMarkdown>;
});

const AssistantResponseBody = ({ data, isPresetTab, presets, onExportPresets, exportDisabled }) => {
  if (isPresetTab) {
    return (
      <PresetResponseBox
        data={data}
        presets={presets}
        handleExportPresets={onExportPresets}
        disabled={exportDisabled}
      />
    );
  }

  if (data?.download_url) {
    return <LargeResponseDownload data={data} />;
  }

  return <MessageMarkdown content={data.content} />;
};

// Keeps the chat input's value in its own component so typing only re-renders
// the textarea, not the whole Chatbot (and its heavy message list). Clears
// itself after a successful send.
const ChatInput = ({ disabled, onSend }) => {
  const [input, setInput] = useState('');

  const submit = (value) => {
    const text = value ?? input;
    if (!text || !text.trim()) return;
    onSend(text);
    setInput('');
  };

  return (
    <ResizableTextArea
      id='viewer-docChat-chat-input'
      value={input}
      onChange={(e) => setInput(e.target.value)}
      disabled={disabled}
      handleSend={submit}
    />
  );
};

const Chatbot = ({ selectedRecord }) => {
  const dispatch = useDispatch();
  const { user_id } = useSelector((state) => state.loginState);
  const { dropdownAndMiscStates } = useTakeTour();
  const [activeTab, setActiveTab] = useLocalStorage(
    DOCCHAT_ACTIVE_TAB_KEY,
    ACTIVE_TAB_MAPPING.chat
  );

  const calculatedActiveTab = dropdownAndMiscStates.chatBoxActiveTab || activeTab;

  const navigate = useNavigate();
  const chatInnerContainerRef = useRef();
  const chatContainerRef = useRef();
  const { userData } = useSelector((state) => state.loginState);
  const { messages, sendMessage, isWaitingForResponse } = useWebSocket(`${DOCCHAT_ROOT_URL}/ws`);
  const [newMessageAdded, setNewMessageAdded] = useState(false);
  const lastMessageRef = useRef(null);
  const spacerRef = useRef(null);
  const [negativeFeedbackInfoSelected, setNegativeFeedbackInfoSelected] = useState([]);
  const [selectedTags, setSelectedTags] = useState('');
  const [feedbackInput, setFeedbackInput] = useState({
    value: '',
    open: false,
  });

  const handleTabChange = useCallback(
    (newTab) => {
      trackButtonClick({ type: `DocChat tab switch - ${newTab}` });
      setActiveTab(newTab);
    },
    [setActiveTab]
  );

  const divRef = useRef(null);
  const inputRef = useRef(null);
  const tagsData = [
    'Incomplete Response',
    'Not relevant to my query',
    'Response could be more detailed or insightful',
    'Took too long to respond',
    'Incorrect interpretation',
    'Other',
  ];

  const {
    presets,
    workflows,
    presetsLoading,
    presetsError,
    presetChat,
    presetResponsesLoading,
    presetResponsesError,
    generatePresetVariables,
    generatePreset,
    generatePresetLoading,
    handlePresetClearChat,
    presetFeedbackPost,
    topUsedPresets,
  } = usePresets();

  const topUsedPresetsList = useMemo(() => {
    return topUsedPresets.map((v) => ({
      ...v,
      id: v.preset_id,
      label: v.preset_name,
      disabled: generatePresetLoading,
      icon: v.preset_type === 'default' ? <MagicIconSVG /> : null,
    }));
  }, [topUsedPresets, generatePresetLoading]);

  const { uuid, recordType } = useSelectedUploadData();

  const {
    handleExport,
    exportRequestLoading: chatExportLoading,
    recordInfoNotFound,
  } = useExportDocument(
    ({ recordType: exportDocumentRecordType, uuid: exportDocumentUuid }) =>
      `api/${exportDocumentRecordType.toLowerCase()}s/${exportDocumentUuid}/export`
  );

  const { handleExport: handleExportPresets, exportRequestLoading: presetExportRequestLoading } =
    useExportDocument(
      ({ recordType: exportDocumentRecordType, uuid: exportDocumentUuid }) =>
        `api/presets/export/${exportDocumentRecordType.toLowerCase()}/${exportDocumentUuid}`
    );

  const { actionState, setCurrentActionState, resetActionState } = useActionState();

  const chatData = useMemo(() => {
    return selectedRecord?.chat_history || [];
  }, [selectedRecord?.chat_history]);

  const isPresetTab = calculatedActiveTab === ACTIVE_TAB_MAPPING.presets;

  const allItems = useMemo(
    () => (isPresetTab ? presetChat : chatData),
    [isPresetTab, presetChat, chatData]
  );

  // Workflow runs are few and each one is a self-contained summary, so paging
  // there would only hide results. The chat transcript is the list that grows
  // without bound, so it's the only one that pages.
  const { visibleItems } = useReverseInfiniteScroll({
    items: allItems,
    scrollElementRef: chatInnerContainerRef,
    pageSize: CHAT_PAGE_SIZE,
    thresholdPx: LOAD_OLDER_THRESHOLD_PX,
    resetKey: `${calculatedActiveTab}:${uuid}`,
    enabled: !isPresetTab,
  });

  const handleExportPresetsFunc = (exportPresetObj) => {
    if (recordType === 'DOCUMENT') {
      handleExportPresets(exportPresetObj);
      return;
    }

    const { successfulDocuments, failedDocuments } = exportPresetObj.data;
    const anaylizedFiles = new Set(
      [...successfulDocuments, ...failedDocuments].map((v) => v.documentID)
    );

    const listOfUnanalyzedFiles = selectedRecord.documents.filter(
      (v) => !anaylizedFiles.has(v.document_id)
    );
    if (listOfUnanalyzedFiles.length === 0) {
      handleExportPresets(exportPresetObj);
      return;
    }

    setCurrentActionState({
      type: actionStateMapping.unanalyzedFilesModal,
      data: {
        listOfUnanalyzedFiles,
        exportPresetObj,
      },
    });
  };

  useEffect(() => {
    if (negativeFeedbackInfoSelected && divRef.current) {
      divRef.current.focus();
      divRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [negativeFeedbackInfoSelected]);

  useEffect(() => {
    if (feedbackInput?.open && inputRef.current) {
      inputRef.current.focus();
      const inputElement = inputRef.current.input || inputRef.current;
      inputElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedTags]);

  useEffect(() => {
    if (messages?.response || messages?.download_url) {
      dispatch(
        setNewMessage({
          timestamp: new Date().toISOString(),
          content: messages.response ?? '',
          role: 'assistant',
          documentId: messages.documentID,
          chat_id: messages.chatID,
          query_response_id: messages.query_response_id,
          collectionID: messages.collectionID,
          download_url: messages.download_url,
          content_length: messages.content_length,
        })
      );
      setNewMessageAdded(true);
    }
  }, [messages]);

  const handleSendMessage = (userMessage) => {
    if (!selectedRecord?.chat_id) {
      message.error('Something went wrong: missing chatId');
      console.error('chat id missing', selectedRecord);
      return;
    }
    trackButtonClick({ type: 'Send Message' });
    if (userMessage.trim()) {
      const messageObj = {
        userID: user_id,
        query: userMessage,
        collectionID: selectedRecord?.collection_id,
        documentID: selectedRecord?.document_id,
        chatID: selectedRecord?.chat_id,
      };
      sendMessage(messageObj);
      dispatch(
        setNewMessage({
          timestamp: new Date().toISOString(),
          content: userMessage,
          chat_id: selectedRecord?.chat_id,
          role: 'user',
          documentId: selectedRecord?.document_id,
          collectionID: selectedRecord?.collection_id,
        })
      );
      setNewMessageAdded(true);
    }
  };

  useEffect(() => {
    if (spacerRef.current) {
      spacerRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'end',
        inline: 'nearest',
      });
    }
  }, [calculatedActiveTab, uuid]);

  useEffect(() => {
    if (calculatedActiveTab !== ACTIVE_TAB_MAPPING.presets) return;
    if (spacerRef.current) {
      spacerRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'end',
        inline: 'nearest',
      });
    }
  }, [presetChat.length, calculatedActiveTab]);

  useEffect(() => {
    if (calculatedActiveTab === ACTIVE_TAB_MAPPING.chat && newMessageAdded) {
      setNewMessageAdded(false);
      setTimeout(() => {
        if (lastMessageRef.current) {
          lastMessageRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 0);
    }
  }, [newMessageAdded, calculatedActiveTab]);

  const handleClearChat = () => {
    trackButtonClick({ type: 'Clear Chat' });
    if (isWaitingForResponse[uuid]) {
      message.info('Please wait for the chat to finish generating');
      return;
    }
    dispatch(
      deleteChat({
        collection_id: selectedRecord?.collection_id,
        documentId: selectedRecord?.document_id,
      })
    );
  };

  const handleFeedback = async ({ tag, query_response_id, run_id }) => {
    if (tag) {
      if (tag === 'Other') {
        setFeedbackInput({
          ...feedbackInput,
          open: !feedbackInput.open,
        });
      }
      setSelectedTags(tag);
    }

    try {
      if (tag !== 'Other') {
        if (run_id) {
          trackButtonClick({ type: `Preset Feedback - ${tag || 'Good Response'}` });
          await presetFeedbackPost({
            run_id,
            feedback: tag || 'Good Response',
          });
        } else if (query_response_id) {
          trackButtonClick({ type: `Chat Feedback - ${tag || 'Good Response'}` });
          await dispatch(
            sendFeedback({
              query_response_id,
              feedback: tag || 'Good Response',
              type: !tag,
              documentId: selectedRecord?.document_id,
              collection_id: selectedRecord?.collection_id,
            })
          );
        }

        setSelectedTags('');
        setFeedbackInput({
          value: '',
          open: false,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleFeedbackClose = () => {
    setNegativeFeedbackInfoSelected([]);
    setSelectedTags('');
    setFeedbackInput({ open: false, value: '' });
  };

  const tabs = [
    {
      label: 'Chat',
      key: ACTIVE_TAB_MAPPING.chat,
      onClick: () => {
        const el = chatInnerContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      },
    },
    {
      label: (
        <InlineStyled
          className='workflow-tab'
          $style={{ position: 'relative' }}
          as={Tooltip}
          title='Select either your custom workflow or a ready-made workflow in the chat input or window to begin'
          placement='bottom'
        >
          Workflow
          <InlineStyled as='span' $style={{ position: 'absolute', right: '-12px', top: '-10px' }}>
            <Badge status='processing' />
          </InlineStyled>
        </InlineStyled>
      ),
      key: ACTIVE_TAB_MAPPING.presets,
      onClick: () => {
        chatInnerContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'auto',
        });
      },
    },
  ];

  // function popupRender() {
  //   return (
  //     <section className="chat-menu-section">
  //       <Dropdown menu={{ items }} trigger={['hover']} placement="left">
  //         <div className="chat-sub-menu-item">
  //           <div>
  //             <DownloadOutlined />
  //             <span>Export</span>
  //           </div>
  //           <AngleRightOutlined />
  //         </div>
  //       </Dropdown>
  //       <div className="chat-menu-item">
  //         <ClearBrushOutlined />
  //         <span>Clear Chat</span>
  //       </div>
  //     </section>
  //   );
  // }

  const exportItems = [
    {
      label: 'Word',
      key: '1',
      icon: (
        <div className='export-menu-icon'>
          <MSWordIcon />
        </div>
      ),
      onClick: () => {
        trackButtonClick({ type: 'Export Docx' });
        handleExport({ export_format: 'docx' });
      },
    },
    {
      label: 'PDF',
      key: '2',
      icon: (
        <div className='export-menu-icon'>
          <PdfIcon2 />
        </div>
      ),
      onClick: () => {
        trackButtonClick({ type: 'Export PDF' });
        handleExport({ export_format: 'pdf' });
      },
    },
  ];

  return (
    <div className='chatbot-container'>
      <section className='chatbot-heading-container flex-row'>
        <AppTabs activeTab={calculatedActiveTab} setActiveTab={handleTabChange} tabs={tabs} />

        <Flex gap='small' id='chatbot-action-buttons'>
          {calculatedActiveTab === ACTIVE_TAB_MAPPING.chat && (
            <>
              <Dropdown menu={{ items: exportItems }} trigger={['hover']} placement='bottomRight'>
                <Tooltip title='Export'>
                  <Button
                    className='chat-action-btn'
                    onClick={(e) => e.stopPropagation()}
                    loading={chatExportLoading}
                    disabled={recordInfoNotFound}
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
              </Dropdown>

              <Tooltip title='Clear Chat'>
                <Button
                  className='chat-action-btn'
                  onClick={handleClearChat}
                  icon={<ClearBrushOutlinedRed />}
                />
              </Tooltip>
            </>
          )}

          {calculatedActiveTab === ACTIVE_TAB_MAPPING.presets && (
            <Tooltip title='Clear workflow Chat'>
              <Button
                className='chat-action-btn'
                onClick={handlePresetClearChat}
                icon={<ClearBrushOutlinedRed />}
              />
            </Tooltip>
          )}
        </Flex>

        {/* <Dropdown menu={{ items: chatItems }} trigger={['click']}>
          <button>
            <ThreeDotOutlined />
          </button>
        </Dropdown> */}
        {/* <div className="chatbot-heading-buttons">
          {activeTab === 'chat' && (
            <>
              <p
                className="chatbot-heading-clear"
                onClick={() => handleClearChat()}
              >
                Clear Chat
              </p>
              <Dropdown
                disabled={exportRequestLoading || recordInfoNotFound}
                className="chatbot-heading-export"
                menu={{ items }}
                overlayStyle={{
                  width: '150px',
                }}
                placement="bottomRight"
              >
                <Button>
                  <Space>
                    Export
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            </>
          )}
        </div> */}
      </section>
      <section className='chatbot-inner-container' ref={chatInnerContainerRef}>
        {calculatedActiveTab === ACTIVE_TAB_MAPPING.chat && (
          <BotInitialChat
            text={
              <>
                <div>Hey {userData?.name}, great to see you!</div>
                <div>
                  I’m Mike DocChat! Your AI assistant for document review and analysis. I can
                  prepare a summary, extract key terms, identify conflicting clauses or highlight
                  clauses that favour one party.
                </div>
                <div>What do you want me to do?</div>
              </>
            }
            buttons={[
              {
                id: '1',
                label: 'Summarise this document',
                disabled: isWaitingForResponse[uuid],
              },
              {
                id: '2',
                label: 'Extract key terms (Term, Termination, Consideration, Liability, Indemnity)',
                disabled: isWaitingForResponse[uuid],
              },
              {
                id: '3',
                label: 'Identify Favourable Clauses of each party to the contract',
                disabled: isWaitingForResponse[uuid],
              },
            ]}
            onButtonClick={(data) => {
              trackButtonClick({ type: `Initial chat button click: ${data.label}` });
              handleSendMessage(data.label);
            }}
          />
        )}
        {calculatedActiveTab === ACTIVE_TAB_MAPPING.presets && (
          <BotInitialChat
            text={
              <>
                <div>Hey {userData?.name}, great to see you!</div>
                <div>
                  Workflows let you run pre-defined prompts to extract key details from one or
                  multiple documents in a structured format.
                </div>
                <div>
                  Choose a workflow below to begin. You can select either your custom workflow or
                  MikeLegal’s Magic preset workflow.
                </div>
                <div>
                  Need a different setup? <b>Click on Create a New Workflow.</b>
                </div>
                <div>Choose or create a Workflow</div>
              </>
            }
            text2={
              <Typography.Paragraph type='secondary' className='mb-0'>
                More workflows in the chat input
              </Typography.Paragraph>
            }
            buttons={[
              {
                id: '1',
                label: 'Create a new Workflow',
                createWorkflowButton: true,
                disabled: generatePresetLoading,
              },
              ...topUsedPresetsList,
            ]}
            onButtonClick={(data) => {
              if (data.createWorkflowButton) {
                trackButtonClick({ type: `Workflow - Initial chat button click: ${data.label}` });
                navigate('/settings/docchat/workflow/create', {
                  state: { fromInternal: true },
                });
                return;
              }
              generatePreset(data.preset_id);
            }}
          />
        )}

        <div className='chatbot-chat-container' id='content' ref={chatContainerRef}>
          {(
            calculatedActiveTab === ACTIVE_TAB_MAPPING.presets
              ? presetChat.length > 0
              : chatData && chatData.length > 0
          ) ? (
            <>
              {/* 'key',
                        el.role === 'user'
                          ? 'user-' + el.timestamp
                          : el.query_response_id || el.run_id */}
              {visibleItems.map(
                (el, index) =>
                  !el?.clear && (
                    <Fragment
                      key={`${el.role}-${el.query_response_id || el.run_id || el.timestamp}`}
                    >
                      <div
                        ref={index === visibleItems.length - 1 ? lastMessageRef : null}
                        className={el.role === 'user' ? 'user-msg' : 'chatbot-msg'}
                      >
                        <div className='msg-inner'>
                          {el.role === 'user' ? (
                            <>
                              <p className='chatbot-para'>
                                <MessageMarkdown content={el.content} />
                              </p>
                              <span className='chatbot-msg-img'>
                                <UserIcon />
                              </span>
                              <p className='chatbot-time'>{timeAgo(el.timestamp)}</p>
                            </>
                          ) : (
                            <>
                              <div className='response-msg'>
                                <AssistantResponseBody
                                  data={el}
                                  isPresetTab={isPresetTab}
                                  presets={[...presets, ...workflows]}
                                  onExportPresets={handleExportPresetsFunc}
                                  exportDisabled={presetExportRequestLoading}
                                />
                              </div>
                              <span className='chatbot-msg-img'>
                                <ChatbotIcon />
                              </span>
                              {el?.role === 'assistant' && (
                                <div className='action-icons'>
                                  {calculatedActiveTab === ACTIVE_TAB_MAPPING.chat &&
                                    !el?.download_url && (
                                      <CopyButton
                                        onCopy={() => {
                                          trackButtonClick({
                                            type: 'Copy - chatbot message',
                                          });
                                          try {
                                            const chatbotMsgElement = chatContainerRef.current
                                              .getElementsByClassName('chatbot-msg')
                                              [
                                                Math.floor(index / 2)
                                              ].querySelector('.response-msg');

                                            return copyToClipboardWithFormat(chatbotMsgElement);
                                          } catch (error) {
                                            console.error('Failed to copy: ', error);
                                            return false;
                                          }
                                        }}
                                      />
                                    )}
                                  {!negativeFeedbackInfoSelected?.includes(
                                    el?.query_response_id || el?.run_id
                                  ) &&
                                    (el?.feedback === 'Good Response' || !el?.feedback) && (
                                      <Tooltip placement='bottom' title='Good Response' arrow>
                                        <span
                                          onClick={() => {
                                            if (el?.feedback) return;
                                            handleFeedback({
                                              query_response_id: el?.query_response_id,
                                              run_id: el?.run_id,
                                            });
                                          }}
                                        >
                                          <LikeIcon
                                            className={
                                              el?.feedback === 'Good Response'
                                                ? 'action-icon feedback-icon-like feedback-icon-like-selected'
                                                : 'action-icon feedback-icon-like'
                                            }
                                          />
                                        </span>
                                      </Tooltip>
                                    )}
                                  {el?.feedback !== 'Good Response' && (
                                    <Tooltip
                                      placement='bottom'
                                      title={el?.feedback || 'Bad Response'}
                                      arrow
                                    >
                                      <span
                                        onClick={() => {
                                          setNegativeFeedbackInfoSelected([
                                            ...negativeFeedbackInfoSelected,
                                            el?.query_response_id || el?.run_id,
                                          ]);
                                        }}
                                      >
                                        <DislikeIcon
                                          className={
                                            el?.feedback && el?.feedback !== 'Good Response'
                                              ? 'action-icon feedback-dislike-icon feedback-dislike-icon-selected'
                                              : 'action-icon feedback-dislike-icon'
                                          }
                                        />
                                      </span>
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {negativeFeedbackInfoSelected?.includes(
                        el?.query_response_id || el?.run_id
                      ) &&
                        !el?.feedback &&
                        el?.role === 'assistant' && (
                          <Card className='feedback-details' ref={divRef}>
                            <CloseOutlined
                              className='feedback-close'
                              onClick={handleFeedbackClose}
                            />
                            <span>Tell us more:</span>
                            <Flex gap={4} wrap>
                              {tagsData.map((tag) => (
                                <div
                                  key={tag}
                                  className={selectedTags === tag ? 'tag tag-selected' : 'tag'}
                                  onClick={() =>
                                    handleFeedback({
                                      tag,
                                      query_response_id: el?.query_response_id,
                                      run_id: el?.run_id,
                                    })
                                  }
                                >
                                  {tag}
                                </div>
                              ))}
                            </Flex>
                            {feedbackInput?.open && (
                              <>
                                <Divider />
                                <span>Provide additional feedback</span>
                                <Input
                                  placeholder='Feel free to add specific details'
                                  className='feedback-input'
                                  onChange={(e) =>
                                    setFeedbackInput({
                                      ...feedbackInput,
                                      value: e.target.value,
                                    })
                                  }
                                  ref={inputRef}
                                />
                                <Flex justify='flex-end'>
                                  <Button
                                    type='primary'
                                    className='feedback-submit'
                                    onClick={() =>
                                      feedbackInput?.value &&
                                      feedbackInput?.value !== '' &&
                                      handleFeedback({
                                        tag: feedbackInput?.value,
                                        query_response_id: el?.query_response_id,
                                        run_id: el?.run_id,
                                      })
                                    }
                                  >
                                    Submit
                                  </Button>
                                </Flex>
                              </>
                            )}
                          </Card>
                        )}
                    </Fragment>
                  )
              )}
              {(calculatedActiveTab === ACTIVE_TAB_MAPPING.presets
                ? generatePresetLoading && generatePresetVariables.ID === uuid
                : isWaitingForResponse[uuid]) && (
                <div className='chatbot-msg' ref={lastMessageRef}>
                  <div className='msg-inner'>
                    <p className='chatbot-waiting-para'>
                      Generating another response{' '}
                      <div className='loading-dots'>
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                      </div>
                    </p>
                    <span className='chatbot-msg-img'>
                      <ChatbotIcon />
                    </span>
                  </div>
                </div>
              )}
              <div ref={spacerRef} style={{ height: '20px' }} />
            </>
          ) : calculatedActiveTab === ACTIVE_TAB_MAPPING.presets && presetResponsesError ? (
            <Typography.Text type='danger'>
              Workflow Response Error:
              {presetResponsesError?.message}
            </Typography.Text>
          ) : null}
        </div>
      </section>
      {calculatedActiveTab === ACTIVE_TAB_MAPPING.presets && (
        <div id='chat-box-workflow-select'>
          {presetsError ? (
            <Typography.Text type='danger'>
              Workflow Error:
              {presetsError?.message}
            </Typography.Text>
          ) : (
            <ChatBoxWorkflowSelect
              loading={generatePresetLoading || presetsLoading || presetResponsesLoading}
              onSend={generatePreset}
            />
          )}
        </div>
      )}
      {calculatedActiveTab === ACTIVE_TAB_MAPPING.chat && (
        <ChatInput disabled={isWaitingForResponse[uuid]} onSend={handleSendMessage} />
      )}

      <Modal
        title='Workflow for these file(s) has not been generated.'
        open={actionState.type === actionStateMapping.unanalyzedFilesModal}
        className='file-upload-error-modal'
        footer={null}
        onCancel={resetActionState}
      >
        <InlineStyled
          as='ol'
          $style={{
            color: 'rgba(0, 0, 0, 0.45)',
            paddingLeft: 30,
            maxHeight: '60vh',
            overflow: 'auto',
          }}
        >
          {actionState.data.listOfUnanalyzedFiles?.map?.((item) => (
            <InlineStyled as='li' $style={{ listStyleType: 'decimal' }} key={item.document_id}>
              {item.document_name}
              {/* <DocumentListItem documentName={item.document_name} /> */}
            </InlineStyled>
          ))}
        </InlineStyled>
        <InlineStyled
          as={Flex}
          justify='space-between'
          $style={{
            marginTop: 16,
          }}
        >
          <AppButton
            color='primary'
            variant='outlined'
            onClick={() => {
              handleExportPresets(actionState.data.exportPresetObj);
              resetActionState();
            }}
            loading={presetExportRequestLoading}
            disabled={presetExportRequestLoading || generatePresetLoading}
          >
            Skip and download
          </AppButton>
          <AppButton
            type='primary'
            loading={generatePresetLoading}
            disabled={presetExportRequestLoading || generatePresetLoading}
            onClick={async () => {
              const copyOfExportPresetObj = { ...actionState.data.exportPresetObj };
              const res = await generatePreset(actionState.data.exportPresetObj.preset_id);
              if (res.status === 200) {
                const { run_id } = res.data;
                handleExportPresets({ ...copyOfExportPresetObj, run_id });
                resetActionState();
              }
            }}
          >
            Generate and download
          </AppButton>
        </InlineStyled>
      </Modal>
    </div>
  );
};

export default Chatbot;
